/**
 * 画笔绘制中的视口 Canvas 渲染器
 * 接收与旧预览相同的笔刷图层，只改变临时位图的尺寸和绘制方式，不参与最终落图
 */
import type { ILayerProps } from '@/version-types';
import { drawBrushPreviewContent } from '../../utils/brush-creator';

/** 瞬态预览的场景到视口变换；不叠加正式图层的页内偏移 */
export interface BrushPreviewViewport {
    width: number;
    height: number;
    zoom: number;
    x: number;
    y: number;
    retina: number;
}

/**
 * 把笔刷图层同步绘制到视口大小的临时画布
 *
 * @param canvas - 仅供当前笔迹使用的 Canvas，函数会调整尺寸并清理内容
 * @param layer - 按旧预览规则计算的笔刷图层
 * @param viewport - 编辑器视口尺寸及场景到视口的变换
 * @returns 是否绘制成功，素材尚未就绪时返回 false
 */
export function paintBrushPreview(canvas: HTMLCanvasElement, layer: ILayerProps, viewport: BrushPreviewViewport) {
    const ctx = canvas.getContext('2d');
    if (!ctx || viewport.width <= 0 || viewport.height <= 0 || viewport.zoom <= 0) return false;
    const { width, height } = layer;
    const retina = Math.max(1, viewport.retina || 1);
    const pixelWidth = Math.ceil(viewport.width * retina);
    const pixelHeight = Math.ceil(viewport.height * retina);
    if (canvas.width !== pixelWidth) canvas.width = pixelWidth;
    if (canvas.height !== pixelHeight) canvas.height = pixelHeight;
    canvas.style.width = `${viewport.width}px`;
    canvas.style.height = `${viewport.height}px`;
    // 整个笔刷统一透明度，不能逐条 stroke 应用，否则交叠处会变深。
    canvas.style.opacity = String(layer.opacity);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.save();
    try {
        ctx.setTransform(
            retina * viewport.zoom,
            0,
            0,
            retina * viewport.zoom,
            retina * (viewport.x + layer.left * viewport.zoom),
            retina * (viewport.y + layer.top * viewport.zoom),
        );
        // 原 BaseOperateView 绕图层中心旋转，直线、箭头和波浪线必须使用相同原点。
        if (layer.rotate) {
            ctx.translate(width / 2, height / 2);
            ctx.rotate((layer.rotate * Math.PI) / 180);
            ctx.translate(-width / 2, -height / 2);
        }
        // 旧预览裁剪于笔迹图层；不能裁剪到画板，否则移出普通/长画板的笔迹会消失。
        ctx.beginPath();
        ctx.rect(0, 0, width, height);
        ctx.clip();
        return drawBrushPreviewContent(ctx, layer);
    } finally {
        ctx.restore();
    }
}
